import { Component, Inject, Input, OnInit, Optional } from '@angular/core';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { ModalService } from '@app/core/services/modal.service';
import { DataTaskService } from '@app/core/services/data-task.service';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { Grid } from '@app/core/components/grid/grid.model';

@Component({
    selector: 'tt-order-head-cap-job',
    templateUrl: './order-head-cap-job.component.html',
    styleUrls: ['./order-head-cap-job.component.css'],
    standalone: false,
})
export class OrderHeadCapJobComponent implements OnInit {
    @Input()
    public params?: GetOrderHeadCapJobParams;

    public page?: OrderHeadCapJobGet;

    public grid?: GridOptions;

    public addingToBackwardPlanning = false;

    public addingToForwardPlanning = false;

    constructor(
        private modal: ModalService,
        private datatask: DataTaskService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    private async loadPage() {
        if (!!this.params) {
            this.page = (await this.datatask.Post(2071, this.params))[0];
        }
    }

    private loadGrid() {
        if (!this.page) return;

        if (!this.grid) {
            this.grid = new Grid()
                .setRememberId(this.page.view_id)
                .setLoadDataFunction({
                    method: this.page.p2_datatask_keyno as number,
                    parameters: () => this.page
                })
                .setKendoConfig({ height: '100%' })
                .setToolbar({ wrapping: true });
        } else {
            this.grid?.gridfunc?.read();
        }
    }

    public async addToBackwardPlanning() {
        if (!this.page) return;

        try {
            this.addingToBackwardPlanning = true;

            const { errorcode, errormessage } = (await this.datatask.Post(2076, { order_internal_no: this.page.order_internal_no }))[0];

            if (errorcode !== '0') {
                this.modal.show({
                    type: 'warning',
                    title: 'error',
                    translateTitle: true,
                    message: errormessage
                });
            } else {
                this.createBackwardPlanning();
            }
        } finally {
            this.addingToBackwardPlanning = false;
        }
    }

    public async addToForwardPlanning() {
        if (!this.page) return;

        try {
            this.addingToForwardPlanning = true;

            const { errorcode, errormessage } = (await this.datatask.Post(2076, { order_internal_no: this.page.order_internal_no }))[0];

            if (errorcode !== '0') {
                this.modal.show({
                    type: 'warning',
                    title: 'error',
                    translateTitle: true,
                    message: errormessage
                });
            } else {
                this.createForwardPlanning();
            }
        } finally {
            this.addingToForwardPlanning = false;
        }
    }

    private async createBackwardPlanning() {
        const currentData = this.grid?.gridfunc?.getAllRows() ?? [];

        if (currentData.length === 0 || !this.page) return;

        let count = 0;

        const progressModal = this.modal.openProgressDialog({
            rememberId: 'w_orderheadcapjob_progressmodal',
            value: count,
            max: currentData.length
        });

        for (let row of currentData) {
            const { errorcode, errormessage } = (await this.datatask.Post(2073, {
                orderline_keyno: row['orderline_keyno'],
                capjob_keyno: row['capjob_keyno'],
                lastdt_production_finish: this.page!.lastdt_production_finish,
                direction: 'B'
            }))[0];

            if ('' + errorcode !== '0') {
                this.addingToBackwardPlanning = false;
                progressModal.updateStatus('invalid');
                progressModal.updateInvalidText(errormessage);
                break;
            }

            count = count + 1;
            progressModal.updateValue(count);
        }

        if (count === currentData.length) {
            this.addingToBackwardPlanning = false;
            progressModal.updateStatus('finish');
        }

        this.loadGrid();
    }

    private async createForwardPlanning() {
        const currentData = this.grid?.gridfunc?.getAllRows() ?? [];

        if (currentData.length === 0 || !this.page) return;

        let count = 0;

        const progressModal = this.modal.openProgressDialog({
            rememberId: 'w_orderheadcapjob_progressmodal',
            value: count,
            max: currentData.length
        });

        for (let row of currentData) {
            const { errorcode, errormessage } = (await this.datatask.Post(2073, {
                orderline_keyno: row['orderline_keyno'],
                capjob_keyno: row['capjob_keyno'],
                lastdt_production_finish: this.page!.lastdt_production_finish,
                direction: 'F'
            }))[0];

            if ('' + errorcode !== '0') {
                this.addingToForwardPlanning = false;
                progressModal.updateStatus('invalid');
                progressModal.updateInvalidText(errormessage);
                break;
            }

            count = count + 1;
            progressModal.updateValue(count);
        }

        if (count === currentData.length) {
            this.addingToForwardPlanning = false;
            progressModal.updateStatus('finish');
        }

        this.loadGrid();
    }

    private async init() {
        await this.loadPage();
        this.loadGrid();
    }

    ngOnInit() {
        if (!!this.navigator) {
            this.params = this.navigator.params as any;

            this.init();
        }
    }
}

interface GetOrderHeadCapJobParams {
    order_internal_no: string;
}

interface OrderHeadCapJobGet {
    page_heading: string;
    order_internal_no: string;
    p2_datatask_keyno: string | number;
    view_id: string;
    deliverydate_wish: string;
    lastdt_production_finish: string;
}
